import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { ApiErrorBody } from '@nova/shared';
import { authStore } from '@/stores/auth.store';

/**
 * The one HTTP client. Every feature imports this — never construct another
 * axios instance and never call `fetch` directly, or the token refresh and the
 * error normalisation stop applying.
 */
export const api: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = authStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const lang = authStore.getState().user?.language ?? 'AR';
  config.headers['Accept-Language'] = lang.toLowerCase();
  return config;
});

/**
 * A burst of parallel queries all 401 at once when the token expires. Refresh
 * once and replay them, rather than firing N refresh calls and racing to write
 * N different tokens into the store.
 */
let refreshPromise: Promise<string | null> | null = null;

export function refreshAccessToken(): Promise<string | null> {
  refreshPromise ??= performRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function performRefresh(): Promise<string | null> {
  const { refreshToken } = authStore.getState();
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post<{ accessToken: string; refreshToken: string }>(
      '/api/auth/refresh',
      { refreshToken },
    );
    authStore.getState().setTokens(data);
    return data.accessToken;
  } catch {
    authStore.getState().clear();
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    if (error.response?.status === 401 && original && !original._retried) {
      original._retried = true;
      const token = await refreshAccessToken();
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      }
      // Refresh failed — the session is genuinely over.
      if (!window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    }

    return Promise.reject(normalizeError(error));
  },
);

/** Everything thrown out of `api` is an ApiError, so callers never branch on axios shapes. */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly messageAr: string,
    readonly messageEn: string,
    readonly statusCode: number,
    readonly details?: Record<string, string[]>,
  ) {
    super(messageEn || messageAr);
    this.name = 'ApiError';
  }

  /** The message to show the user, in their language. */
  localized(lang: 'AR' | 'EN' = 'AR'): string {
    return lang === 'AR' ? this.messageAr : this.messageEn || this.messageAr;
  }
}

function normalizeError(error: AxiosError<ApiErrorBody>): ApiError {
  const body = error.response?.data;
  if (body?.code) {
    return new ApiError(body.code, body.messageAr, body.messageEn, body.statusCode, body.details);
  }
  if (error.code === 'ECONNABORTED') {
    return new ApiError('TIMEOUT', 'انتهت مهلة الطلب', 'Request timed out', 408);
  }
  if (!error.response) {
    return new ApiError('NETWORK_ERROR', 'تعذّر الاتصال بالخادم', 'Cannot reach the server', 0);
  }
  return new ApiError(
    'UNKNOWN',
    'حدث خطأ غير متوقع',
    error.message ?? 'Unexpected error',
    error.response.status,
  );
}

/** Message helper for toasts. */
export function errorMessage(error: unknown, lang: 'AR' | 'EN' = 'AR'): string {
  if (error instanceof ApiError) return error.localized(lang);
  if (error instanceof Error) return error.message;
  return lang === 'AR' ? 'حدث خطأ غير متوقع' : 'Unexpected error';
}

/** NFR-11 — download an Excel/PDF export from a report endpoint. */
export async function downloadExport(url: string, params: Record<string, unknown>, filename: string): Promise<void> {
  const response = await api.get(url, { params, responseType: 'blob' });
  const blob = new Blob([response.data]);
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}
